import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';

const About = () => {
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/settings`);
        setSettings(response.data);
      } catch (error) {
        console.error('Error fetching settings:', error);
      }
    };
    fetchSettings();
  }, []);

  const defaultAboutContent = `
    STUDY POINT is a premier educational platform dedicated to empowering students from class 6th to 12th. 
    Our mission is to provide high-quality education that is accessible, engaging, and effective.

    Founded with the vision of making quality education available to all, STUDY POINT offers comprehensive 
    learning materials including video lectures, study notes, practice papers, and interactive content 
    across all major subjects and streams.

    Our team of experienced educators and technology experts work together to create learning experiences 
    that are not only informative but also inspiring. We believe that every student has the potential to 
    excel, and we are committed to helping them achieve their academic goals.

    What sets us apart:
    • Expert Faculty: Our content is created by subject matter experts with years of teaching experience
    • Comprehensive Coverage: From basic concepts to advanced topics, we cover it all
    • AI-Powered Learning: Our Dauter AI assistant helps students with personalized learning support
    • Interactive Content: Engaging videos, animations, and practice exercises
    • Accessible Anywhere: Learn on any device, anytime, anywhere

    Join thousands of students who have transformed their academic journey with STUDY POINT. 
    Together, let's embark on a journey towards excellence!
  `;

  const topics = [
    {
      title: 'Education',
      content: 'Education is the cornerstone of personal and societal growth. It empowers individuals with knowledge, skills, and values necessary to navigate life successfully. At STUDY POINT, we believe education should be accessible, engaging, and transformative.'
    },
    {
      title: 'Psychology of Education',
      content: 'Understanding how students learn is crucial to effective teaching. Educational psychology explores cognitive development, motivation, and learning styles. We apply these principles to create content that resonates with diverse learners and maximizes retention.'
    },
    {
      title: 'Meaning of Education',
      content: 'True education goes beyond memorizing facts. It is about developing critical thinking, fostering curiosity, and building character. Education prepares students not just for exams, but for life - equipping them with the tools to solve problems, make informed decisions, and contribute positively to society.'
    }
  ];

  return (
    <div className="pt-16 min-h-screen bg-[#050505]" data-testid="about-page">
      {/* Hero Section */}
      <section className="relative py-20">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{
            backgroundImage: 'url(https://images.pexels.com/photos/6283211/pexels-photo-6283211.jpeg)',
          }}
        ></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="text-sm uppercase tracking-[0.2em] font-semibold text-zinc-500">About Us</span>
          <h1 className="font-outfit text-4xl md:text-5xl font-black text-white mt-2 mb-6">
            Our <span className="text-red-500">Story</span>
          </h1>
          <p className="text-zinc-400 max-w-3xl leading-relaxed whitespace-pre-line">
            {settings?.about_content || defaultAboutContent}
          </p>
        </div>
      </section>

      {/* Owner Section */}
      <section className="py-20 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="order-2 md:order-1">
              <span className="text-sm uppercase tracking-[0.2em] font-semibold text-red-500">Founder</span>
              <h2 className="font-outfit text-3xl md:text-4xl font-bold text-white mt-2 mb-6">
                {settings?.owner_name || 'Sid Thakur'}
              </h2>
              <p className="text-zinc-400 leading-relaxed">
                With a passion for education and a vision to make quality learning accessible to all, 
                our founder established STUDY POINT to bridge the gap between traditional education 
                and modern learning needs. Their dedication to student success drives every aspect 
                of our platform.
              </p>
              <div className="mt-6 flex gap-4">
                <div className="text-center">
                  <div className="font-outfit text-3xl font-bold text-white">10K+</div>
                  <div className="text-sm text-zinc-500">Students</div>
                </div>
                <div className="text-center">
                  <div className="font-outfit text-3xl font-bold text-white">5+</div>
                  <div className="text-sm text-zinc-500">Years</div>
                </div>
                <div className="text-center">
                  <div className="font-outfit text-3xl font-bold text-white">500+</div>
                  <div className="text-sm text-zinc-500">Resources</div>
                </div>
              </div>
            </div>
            <div className="order-1 md:order-2">
              <div className="relative">
                <div className="absolute -inset-4 bg-red-500/20 rounded-sm blur-2xl"></div>
                <img
                  src={settings?.owner_photo || 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400'}
                  alt={settings?.owner_name || 'Sid Thakur'}
                  className="relative w-full max-w-md mx-auto rounded-sm border border-zinc-800"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Topics Section */}
      <section className="py-20 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-sm uppercase tracking-[0.2em] font-semibold text-zinc-500">Philosophy</span>
            <h2 className="font-outfit text-3xl md:text-4xl font-bold text-white mt-2">
              Our <span className="text-red-500">Approach</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {topics.map((topic, idx) => (
              <div
                key={idx}
                className={`card-hover bg-[#121212] p-8 rounded-sm animate-fadeIn stagger-${idx + 1}`}
                data-testid={`topic-${idx}`}
              >
                <div className="w-12 h-12 rounded-sm bg-red-500/10 flex items-center justify-center mb-6">
                  <span className="font-outfit text-2xl font-bold text-red-500">{idx + 1}</span>
                </div>
                <h3 className="font-outfit text-xl font-bold text-white mb-4">{topic.title}</h3>
                <p className="text-zinc-400 leading-relaxed">{topic.content}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-20 bg-[#0A0A0A]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-sm uppercase tracking-[0.2em] font-semibold text-red-500">Our Mission</span>
          <h2 className="font-outfit text-3xl md:text-4xl font-bold text-white mt-2 mb-6">
            Journey Towards Excellence
          </h2>
          <p className="text-xl text-zinc-400 leading-relaxed">
            "To empower every student with the knowledge, skills, and confidence they need to 
            succeed in their academic journey and beyond. We are committed to making quality 
            education accessible, engaging, and transformative."
          </p>
        </div>
      </section>
    </div>
  );
};

export default About;
